import { getObjectEntries } from '@myre/common-primitives';
import { SeparatorComponent } from '@myre/ui-primitive-components';
import {
  markAsTouched,
  QuestionBase,
  QuestionControlService,
  type ElementBase,
} from '@myre/ui-primitive-forms';

import {
  booleanAttribute,
  Component,
  computed,
  DestroyRef,
  HostBinding,
  inject,
  input,
  inputBinding,
  isDevMode,
  outputBinding,
  reflectComponentType,
  signal,
  Type,
  viewChild,
  ViewContainerRef,
  type Binding,
  type ComponentRef,
  type EnvironmentInjector,
  type Injector,
  type OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import {
  type IModalComponent,
  type IModalData,
  type ModalBindableSection,
  type ModalBindings,
} from '../modal.interface';
import { ModalService } from '../modal.service';

import { DefaultBodyComponent } from './templates/bodies/app-modal-body-default.component';
import { DefaultFooterComponent } from './templates/footers/app-modal-footer-default.component';
import { DefaultHeaderComponent } from './templates/headers/app-modal-header-default.component';

@Component({
  selector: 'myre-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, SeparatorComponent],
})
export class ModalComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _modalService = inject(ModalService);
  private _questionControlService = inject(QuestionControlService);

  public body = viewChild('body', { read: ViewContainerRef });
  public header = viewChild('header', { read: ViewContainerRef });
  public footer = viewChild('footer', { read: ViewContainerRef });

  public modal = input<IModalData>();

  public first = input(false, { transform: booleanAttribute });

  protected isFull = computed(() => this.modal().size === 'full');
  protected isXLarge = computed(() => this.modal().size === 'xlarge');
  protected isLarge = computed(() => this.modal().size === 'large');
  protected isMedium = computed(() => this.modal().size === 'medium');

  protected isInfo = computed(() => this.modal().type === 'info');
  protected isOther = computed(() => this.modal().type === 'other');
  protected isWarning = computed(() => this.modal().type === 'warning');
  protected isSidebar = computed(() => this.modal().type === 'sidebar');

  protected isLeftSidebar = computed(() => this.modal().sidebarFrom === 'left');
  protected isRightSidebar = computed(
    () => this.modal().sidebarFrom === 'right',
  );

  public sidebarWidth = computed(() => this.modal().sidebarWidth);
  public closeButtonWidth = computed(() => {
    const paddings = 16;
    const width = this.modal().sidebarWidth ?? 0;

    return width + paddings;
  });

  public elements$: Observable<ElementBase[]>;
  public isLoading$: Observable<boolean>;

  public get formGroup(): UntypedFormGroup {
    if (this.modal()?.context?.formGroup) {
      return this.modal().context.formGroup;
    }

    return new UntypedFormGroup({});
  }

  public ngOnInit(): void {
    if (this.modal().type === 'warning' || this.modal().type === 'info') {
      this.modal().header = undefined;
      this.modal().size = 'medium';

      const prefix = '_system.yesNoModal';
      const cancel = this.modal().footer?.data.cancel || `${prefix}.cancel`;
      const confirm = this.modal().footer?.data.ok;

      if (this.modal().type === 'warning') {
        this.modal().footer = {
          data: {
            cancel,
            confirmWarning: confirm || `${prefix}.confirmWarning`,
          },
        };
      } else {
        this.modal().footer = {
          data: {
            cancel,
            confirmInfo: confirm || `${prefix}.confirmInfo`,
          },
        };
      }
    }

    const headerComponentRef = this._createComponentRef(
      this.modal()?.header?.component ?? DefaultHeaderComponent,
      this.header(),
      { bindings: this.modal()?.header?.bindings },
    );

    const bodyComponentRef = this._createComponentRef(
      this.modal()?.body?.component ?? DefaultBodyComponent,
      this.body(),
      {
        bindings: this.modal()?.body?.bindings,
        elementInjector: this.modal()?.body?.elementInjector,
        environmentInjector: this.modal()?.body?.environmentInjector,
      },
    );

    const footerComponentRef = this._createComponentRef(
      this.modal()?.footer?.component ?? DefaultFooterComponent,
      this.footer(),
      { bindings: this.modal()?.footer?.bindings },
    );

    const headerComponent: IModalComponent = headerComponentRef.instance;
    const bodyComponent: IModalComponent = bodyComponentRef.instance;
    const footerComponent: IModalComponent = footerComponentRef.instance;

    if (this.modal().header) {
      this.modal().header.componentRef = headerComponentRef;
    }

    if (this.modal().body) {
      this.modal().body.componentRef = bodyComponentRef;
    }

    if (this.modal().footer) {
      this.modal().footer.componentRef = footerComponentRef;
    }

    if (this.modal().body && this.modal().body.component) {
      Object.defineProperty(this.modal().context, 'formGroup', {
        get(): UntypedFormGroup {
          return bodyComponent.form;
        },
      });
    } else {
      this.elements$ = this.modal().context.elements || of([]);

      this.isLoading$ = this.elements$.pipe(
        map(els => {
          if (!els) {
            return false;
          }

          return !els.length;
        }),
      );

      bodyComponent.isLoading$ = this.isLoading$;
      footerComponent.isLoading$ = this.isLoading$;

      this.elements$
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(els => {
          this.modal().context.formGroup =
            this._questionControlService.generateForm(els || []);
        });
    }
  }

  public onSubmit(): void {
    this.elements$ = this.modal().context.elements || of([]);

    combineLatest([this.elements$])
      .pipe(takeUntilDestroyed(this._destroyRef), take(1))
      .subscribe(([els]: [ElementBase<any>[]]) => {
        els ||= [];

        const values = this._questionControlService.toValues(els, true);

        if (this.formGroup.valid) {
          if (
            this.modal().context &&
            'function' === typeof this.modal().context.onSubmit
          ) {
            this._modalService.markResolved(this.modal().uid);

            return this.modal().context.onSubmit(
              values,
              els.filter(el => el instanceof QuestionBase),
            );
          }
        } else {
          markAsTouched(this.formGroup);
        }
      });
  }

  public closeModal(): void {
    this._modalService.close(this.modal().uid);
  }

  @HostBinding('style.left.px')
  public get leftOffset(): number | string {
    if (this.isLeftSidebar()) {
      return this.modal().sidebarOffset;
    }

    return 'unset';
  }

  @HostBinding('style.right.px')
  public get rightOffset(): number | string {
    if (this.isRightSidebar()) {
      return this.modal().sidebarOffset;
    }

    return 'unset';
  }

  /** Stable selector for E2E — see {@link IConstructorModalData.key}. */
  @HostBinding('attr.data-testid')
  public get testId(): string | undefined {
    return this.modal()?.key;
  }

  protected _createComponentRef<C = unknown>(
    type: Type<C>,
    ref: ViewContainerRef,
    options?: {
      bindings?: ModalBindings;
      elementInjector?: Injector;
      environmentInjector?: EnvironmentInjector;
    },
  ): ComponentRef<C> {
    return ref.createComponent(type, {
      injector: options?.elementInjector,
      environmentInjector: options?.environmentInjector,
      bindings: this._createBindings(type, options?.bindings ?? {}),
    });
  }

  protected _createBindings<T = unknown>(
    type: Type<T>,
    bindings?: ModalBindings,
  ): Binding[] {
    const result: Binding[] = [];
    const mirror = reflectComponentType(type);

    if (mirror) {
      const availableInputs = new Set(mirror.inputs.map(i => i.templateName));
      const availableOutputs = new Set(mirror.outputs.map(o => o.templateName));
      const { inputs, outputs } = this._fillBindings({ bindings });

      for (const [key, value] of getObjectEntries(inputs)) {
        if (availableInputs.has(key)) {
          result.push(inputBinding(key, signal(value)));
        } else if (isDevMode()) {
          console.warn(
            `[ModalComponent] Input "${key}" does not exist in ${type.name}`,
          );
        }
      }

      for (const [key, handler] of getObjectEntries(outputs)) {
        if (availableOutputs.has(key)) {
          result.push(outputBinding(key, handler));
        } else if (isDevMode()) {
          console.warn(
            `[ModalComponent] Output "${key}" does not exist in ${type.name}`,
          );
        }
      }
    }

    return result;
  }

  protected _fillBindings(section: ModalBindableSection): ModalBindings {
    return {
      outputs: section.bindings?.outputs ?? {},
      inputs: {
        ...(section.bindings?.inputs ?? {}),
        uid: this.modal().uid,
      },
    };
  }
}
